import React, { createContext, useContext, useState, useEffect } from 'react';

// Define la URL base de tu API usando la variable de entorno
// Si no está definida (ej. en algunos entornos de prueba), se usará '/' como fallback,
// pero esto debería estar configurado correctamente en tu .env y en producción.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 1. Crea el Contexto
const NonceContext = createContext(null);

// 2. Crea el Hook personalizado para consumir el Contexto
export function useNonce() {
  const context = useContext(NonceContext);
  if (context === null) {
    throw new Error('useNonce must be used within a NonceProvider');
  }
  return context;
}

// 3. Crea el Proveedor del Contexto
export function NonceProvider({ children }) {
  const [nonce, setNonce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNonceFromApi = async () => {
      try {
        // Construye la URL completa del endpoint
        const nonceEndpoint = `${API_BASE_URL}/api/nonce/`;
        console.log(`Attempting to fetch nonce from: ${nonceEndpoint}`); // Para depuración

        const response = await fetch(nonceEndpoint, { credentials: 'same-origin' });

        if (!response.ok) {
          // Si la respuesta no es OK (ej. 404, 500), intentar leerla como texto para más info
          const errorText = await response.text();
          throw new Error(`HTTP error! Status: ${response.status}. Response: ${errorText}`);
        }

        const data = await response.json(); // Asume que el endpoint devuelve JSON como {"nonce": "..."}

        if (data && data.nonce) { // Verificar que 'data' no sea null/undefined y tenga 'nonce'
          setNonce(data.nonce);
          setLoading(false);
        } else {
          setError("API did not provide a nonce in the expected JSON format (e.g., {'nonce': 'value'}).");
          setLoading(false);
          console.error("Nonce API Error:", "Nonce not found in response data or data is malformed.");
        }
      } catch (err) {
        setError(`Failed to fetch nonce from API: ${err.message}`);
        setLoading(false);
        console.error("Error fetching nonce from API:", err);
      }
    };

    fetchNonceFromApi();
  }, []); // El efecto se ejecuta una sola vez al montar el proveedor

  // El valor que se pasará a los consumidores del contexto
  const contextValue = {
    nonce,
    loading,
    error
  };

  return (
    <NonceContext.Provider value={contextValue}>
      {children}
    </NonceContext.Provider>
  );
}